
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Sidebar from './Sidebar';

type PathTitleMapping = {
  [key: string]: string;
};

const Layout: React.FC = () => {
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState<string>('');
  const [userName, setUserName] = useState<string>("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  // Synchronously compute access control
  const hasAccess = React.useMemo(() => {
    const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true";
    if (isSuperAdmin) return true;

    const permissionsStr = localStorage.getItem("permissions");
    if (permissionsStr) {
      try {
        const permissions = JSON.parse(permissionsStr);
        return permissions.includes(location.pathname);
      } catch (error) {
        console.error("Error parsing permissions:", error);
        return false;
      }
    }
    return false;
  }, [location.pathname]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Inactivity / Auto-Logout Logic
  useEffect(() => {
    let logoutTimer: any;
    const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes in milliseconds

    const handleLogout = async () => {
      console.log("Logging out due to inactivity...");
      try {
        const token = localStorage.getItem("token");
        if (token) {
          // Dynamic import of API_BASE if needed, or just use it if available
          // Assuming it's in ../config based on Sidebar.tsx
          const { API_BASE } = await import('../config');
          await fetch(`${API_BASE}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }
      } catch (error) {
        console.error("Error during auto-logout audit:", error);
      }
      localStorage.clear();
      window.location.href = '/login';
    };

    const resetTimer = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      logoutTimer = setTimeout(handleLogout, INACTIVITY_LIMIT);
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Initial timer start
    resetTimer();

    return () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, []);

  // Update UI state on path change
  useEffect(() => {
    // Close mobile menu on path change
    setIsMobileMenuOpen(false);

    // Get user name
    const name = localStorage.getItem("name");
    setUserName(name || "Admin User");
  }, [location.pathname]);

  const pathTitles: PathTitleMapping = {
    '/': 'Dashboard',
    '/users': 'Users Management',
    '/teamDetails': 'Team Details',
    '/teamincome': 'Team Income Analytics',
    '/income': 'Personal Income',
    '/referal': 'Referral Program',
    '/transferDetails': 'Transfer History',
    '/wallet': 'Digital Wallet',
    '/invest': 'Investment Portal',
    '/investHistory': 'Investment History',
    '/deposit': 'Deposit Management',
    '/withdraw': 'Withdrawal Requests',
    '/withdrawData': 'Investment Withdrawal',
    '/banner': 'Banner Management',
    '/kyc': 'KYC Verification',
    '/ticket': 'Support Tickets',
    '/platinumData': 'Platinum Members',
    '/platinumIncome': 'Platinum Income',
    '/platinumIncomeTeam': 'Platinum Team Income',
    '/notification': 'Notification Management',
    '/appVersion': 'App Version Management',
    '/roles': 'Roles & Responsibilities',
    '/blogs': 'Blogs',
    '/gwccoin': "GWC Coin",
    '/rank': "Rank",
    '/mail': "Mailer",
    '/incomeStat': "Income Statistics",
    '/shiftHistory': "Shift History",
    '/platinumHistory': "Platinum History",
    '/audit-logs': 'System Audit Logs',
    '/wallet-backup': 'Wallet Backup & Reset'
  };

  const getTopBarTitle = (pathname: string): string => {
    return pathTitles[pathname] || 'Growwincapital Dashboard';
  };

  // If no access, redirect to dashboard or access denied page
  if (!hasAccess && location.pathname !== '/') {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className='flex h-[100dvh] bg-gray-50 overflow-hidden relative'>
      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Responsive */}
      <div className={`fixed inset-y-0 left-0 z-50 transform lg:relative lg:translate-x-0 transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:flex`}>
        <Sidebar onMobileClose={() => setIsMobileMenuOpen(false)} />
      </div>

      <div className='flex-1 overflow-auto relative'>
        {/* Modern App Bar */}
        <div className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-10">
          <div className="max-w-full mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Left side - Burger & Title */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="lg:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  aria-label="Open menu"
                >
                  <Menu size={24} />
                </button>
                <h1 className="text-lg md:text-xl font-semibold text-[#523680] truncate max-w-[200px] md:max-w-none">
                  {getTopBarTitle(location.pathname)}
                </h1>
              </div>

              {/* Right side - User Info & DateTime */}
              <div className="flex items-center gap-3 md:gap-6">
                {/* User Display */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#523680] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                    {(userName || 'A').charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-gray-700 hidden sm:inline">{userName}</span>
                </div>

                {/* DateTime - Hidden on Small Screens */}
                <div className="hidden md:flex flex-col items-end space-y-0.5">
                  <div className="text-sm md:text-base font-medium text-[#523680]">
                    {new Date().toLocaleDateString('en-GB', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="text-xs text-[#523680]/80 font-mono tracking-wide">
                    {currentTime}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className='p-4 md:p-6'>
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 md:p-6">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;